// Shared utilities for distinguishing browser-induced teardown of our module
// workers (Chrome Memory Saver, tab discard, freezer, offline, captive portal)
// from genuine worker-load failures. Per the HTML spec, when a module worker
// fails to *load* the browser fires a generic `ErrorEvent` with no `message`,
// `filename`, `lineno`, or `colno`. We treat that shape, when combined with
// strong tab-suspension or offline signals, as "the browser killed us" and
// suppress noisy Sentry reports for it — they are not actionable.

let tabEverHidden = typeof document !== 'undefined' && document.hidden;

if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) tabEverHidden = true;
  });
}

export const wasTabEverHidden = (): boolean => tabEverHidden;

export const isLikelyBrowserAbort = (e: ErrorEvent): boolean => {
  const noDetails = !e.message;
  if (!noDetails) return false;
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  return tabEverHidden || offline;
};

export const formatWorkerErrorMessage = (e: ErrorEvent, fallback: string): string => {
  const message = e.message || fallback;
  const detail = e.filename ? ` at ${e.filename}:${e.lineno}:${e.colno}` : '';
  return `${message}${detail}`;
};

// Error name used by worker shells when they reject their init promise after a
// browser-induced abort. `sentry.ts` `beforeSend` drops events carrying this
// name so a rejected init promise that bubbles up to React's error boundary
// (or to an unhandled-rejection breadcrumb) does not become a Sentry "error".
export const BROWSER_INDUCED_ABORT_NAME = 'BrowserInducedAbort';

export const createBrowserInducedAbortError = (message: string): Error => {
  const error = new Error(message);
  error.name = BROWSER_INDUCED_ABORT_NAME;
  return error;
};
